import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Apple, Bot, CheckCircle2, Share, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/lib/i18n/locale-context";

type Platform = "ios" | "android" | "other";

type InstallEvent = Event & { prompt: () => Promise<void> };

/**
 * Visar hur man lägger Sellavo på hemskärmen, anpassat efter telefonen man sitter på.
 * Kan Chrome installera direkt visas en riktig knapp i stället för instruktionerna.
 */
export function InstallAppGuide() {
  const { lang } = useLocale();
  const tx = lang === "en" ? {
    installed: "You already use Sellavo as an app", installedHint: "Great! You can also enable notifications and know immediately when someone contacts you.", settings: "Go to notification settings",
    ios: "iPhone and iPad", iosSteps: ["Open sellavo.com in Safari (not Chrome or an in-app browser).", "Tap the share icon at the bottom of the screen.", "Scroll down and choose Add to Home Screen.", "Tap Add in the top right. Sellavo now appears among your apps."], iosNote: "On iPhone, the app must be on the home screen before notifications can be enabled.",
    android: "Android", androidSteps: ["Open sellavo.com in Chrome.", "Tap the three-dot menu in the top right.", "Choose Install app or Add to Home screen.", "Confirm, then open Sellavo from the icon."], androidNote: "Some phones show an installation prompt directly at the bottom instead.",
    install: "Install Sellavo now", installHint: "Your browser can add Sellavo for you with one tap.", add: "Add to Home Screen", why: "Why? As an app, Sellavo opens full screen, loads faster and can notify you about messages and higher bids.",
  } : lang === "es" ? {
    installed: "Ya usas Sellavo como aplicación", installedHint: "¡Perfecto! También puedes activar las notificaciones para saber al instante cuándo te contactan.", settings: "Ir a los ajustes de notificaciones",
    ios: "iPhone y iPad", iosSteps: ["Abre sellavo.com en Safari (no en Chrome ni en el navegador de otra aplicación).", "Pulsa el icono de compartir en la parte inferior.", "Desplázate y elige Añadir a pantalla de inicio.", "Pulsa Añadir arriba a la derecha. Sellavo aparecerá entre tus aplicaciones."], iosNote: "En iPhone, la aplicación debe estar en la pantalla de inicio para poder activar las notificaciones.",
    android: "Android", androidSteps: ["Abre sellavo.com en Chrome.", "Pulsa el menú de tres puntos arriba a la derecha.", "Elige Instalar aplicación o Añadir a pantalla de inicio.", "Confirma y abre Sellavo desde el icono."], androidNote: "Algunos teléfonos muestran directamente un aviso de instalación en la parte inferior.",
    install: "Instalar Sellavo ahora", installHint: "Tu navegador puede añadir Sellavo con un solo toque.", add: "Añadir a pantalla de inicio", why: "¿Por qué? Como aplicación, Sellavo se abre a pantalla completa, carga más rápido y puede avisarte de mensajes y pujas superiores.",
  } : {
    installed: "Du kör redan Sellavo som app", installedHint: "Bra! Då kan du också slå på notiser och få veta direkt när någon hör av sig.", settings: "Gå till notisinställningar",
    ios: "iPhone och iPad", iosSteps: ["Öppna sellavo.com i Safari (inte i Chrome eller en app-webbläsare).", "Tryck på dela-ikonen längst ner på skärmen.", "Bläddra ner och välj Lägg till på hemskärmen.", "Tryck Lägg till uppe till höger. Nu ligger Sellavo som en ikon bland dina appar."], iosNote: "På iPhone måste appen ligga på hemskärmen för att notiser ska gå att slå på.",
    android: "Android", androidSteps: ["Öppna sellavo.com i Chrome.", "Tryck på menyn med tre punkter uppe till höger.", "Välj Installera app eller Lägg till på hemskärmen.", "Bekräfta – sedan öppnar du Sellavo från ikonen."], androidNote: "Vissa telefoner visar i stället en installationsruta direkt längst ner.",
    install: "Installera Sellavo direkt", installHint: "Din webbläsare kan lägga till Sellavo åt dig med ett tryck.", add: "Lägg till på hemskärmen", why: "Varför? Som app öppnas Sellavo i helskärm, laddar snabbare och kan skicka notiser när du får ett meddelande eller blir överbjuden.",
  };
  const [platform, setPlatform] = useState<Platform>("other");
  const [installed, setInstalled] = useState(false);
  const [promptEvent, setPromptEvent] = useState<InstallEvent | null>(null);

  useEffect(() => {
    const ua = navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
    setPlatform(iOS ? "ios" : /Android/i.test(ua) ? "android" : "other");
    setInstalled(window.matchMedia("(display-mode: standalone)").matches);

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setPromptEvent(e as InstallEvent);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  if (installed) {
    return (
      <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
        <p className="flex items-center gap-2 font-semibold text-foreground">
          <CheckCircle2 className="h-5 w-5 text-primary" /> {tx.installed}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {tx.installedHint}
        </p>
        <Link
          to="/konto"
          className="mt-2 inline-block text-sm font-semibold text-primary hover:underline"
        >
          {tx.settings}
        </Link>
      </div>
    );
  }

  const ios = (
    <Guide
      icon={<Apple className="h-5 w-5" />}
      title={tx.ios}
      steps={tx.iosSteps}
      note={tx.iosNote}
      badge={<Share className="h-4 w-4" />}
    />
  );

  const android = (
    <Guide
      icon={<Bot className="h-5 w-5" />}
      title="Android"
      steps={tx.androidSteps}
      note={tx.androidNote}
      badge={<MoreVertical className="h-4 w-4" />}
    />
  );

  return (
    <div className="space-y-4">
      {promptEvent && (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
          <p className="font-semibold text-foreground">{tx.install}</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {tx.installHint}
          </p>
          <Button
            className="mt-3"
            onClick={() => {
              void promptEvent.prompt();
              setPromptEvent(null);
            }}
          >
            {tx.add}
          </Button>
        </div>
      )}
      {platform === "android" ? (
        <>
          {android}
          {ios}
        </>
      ) : (
        <>
          {ios}
          {android}
        </>
      )}
      <p className="text-sm text-muted-foreground">
        {tx.why}
      </p>
    </div>
  );
}

function Guide({
  icon,
  title,
  steps,
  note,
  badge,
}: {
  icon: React.ReactNode;
  title: string;
  steps: string[];
  note: string;
  badge: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="flex items-center gap-2 font-heading font-bold text-foreground">
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
          {icon}
        </span>
        {title}
        <span className="ml-auto text-muted-foreground">{badge}</span>
      </p>
      <ol className="mt-3 space-y-2">
        {steps.map((s, i) => (
          <li key={s} className="flex gap-3 text-sm text-foreground">
            <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-secondary text-xs font-bold text-secondary-foreground">
              {i + 1}
            </span>
            <span>{s}</span>
          </li>
        ))}
      </ol>
      <p className="mt-3 text-xs text-muted-foreground">{note}</p>
    </div>
  );
}
